import { useCallback, useEffect, useRef, useState } from 'react';
import { api, useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/use-toast';

const isDev = process.env.NODE_ENV === 'development';
const RESEND_COOLDOWN_S = 30;

export function useAuthFlow(onSuccess) {
  const { login } = useAuth();
  const { toast } = useToast();

  const [step, setStep] = useState('phone');
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [otp, setOtp] = useState('');
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [normalizedPhone, setNormalizedPhone] = useState('');
  const [smsMock, setSmsMock] = useState(false);
  const [devCode, setDevCode] = useState('');
  const [resendIn, setResendIn] = useState(0);
  const timerRef = useRef();

  useEffect(() => {
    if (resendIn <= 0) return undefined;
    timerRef.current = setTimeout(() => setResendIn((v) => v - 1), 1000);
    return () => clearTimeout(timerRef.current);
  }, [resendIn]);

  const reset = useCallback(() => {
    setStep('phone');
    setOtp('');
    setResendIn(0);
  }, []);

  const sendOtp = useCallback(async () => {
    if (!phone.trim()) {
      toast({ title: 'Numéro requis', description: 'Entrez votre numéro de téléphone.' });
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post('/auth/send-otp', { phone });
      setNormalizedPhone(data.phone);
      setSmsMock(!!data.mock);
      setDevCode(data.devCode || '');
      setStep('otp');
      setResendIn(RESEND_COOLDOWN_S);
      toast({
        title: data.mock ? 'Code envoyé (mode développement)' : 'Code envoyé par SMS',
        description: data.mock
          ? `Aucun SMS réel n'est envoyé pour l'instant. Utilisez le code : ${data.devCode}`
          : `Un code à 6 chiffres a été envoyé au ${data.phone}. Vérifiez vos SMS.`,
      });
    } catch (err) {
      if (isDev) console.warn('[auth] send-otp failed', err);
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Impossible d\'envoyer le code.' });
    } finally {
      setSending(false);
    }
  }, [phone, toast]);

  const verifyOtp = useCallback(async (code) => {
    const c = code || otp;
    if (!c || c.length < 6) return;
    setVerifying(true);
    try {
      const { data } = await api.post('/auth/verify-otp', {
        phone: normalizedPhone || phone,
        otp: c,
        name: name || undefined,
      });
      login(data.token, data.user);
      toast({ title: 'Connecté', description: `Bienvenue ${data.user.name || data.user.phone} !` });
      if (onSuccess) onSuccess();
    } catch (err) {
      if (isDev) console.warn('[auth] verify-otp failed', err);
      toast({ title: 'Code incorrect', description: err?.response?.data?.detail || 'Veuillez réessayer.' });
      setOtp('');
    } finally {
      setVerifying(false);
    }
  }, [otp, phone, normalizedPhone, name, login, toast, onSuccess]);

  return {
    step, setStep,
    phone, setPhone,
    name, setName,
    otp, setOtp,
    normalizedPhone,
    smsMock, devCode,
    sending, verifying,
    resendIn,
    sendOtp, verifyOtp,
    reset,
  };
}
